import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { sampleTipsters } from "@/data/sampleData";
import { 
  Trophy, 
  Medal, 
  Award, 
  TrendingUp,
  Filter,
  Calendar,
  Globe,
  Zap,
  Crown,
  Flame
} from "lucide-react";

interface LeaderboardEntry {
  rank: number;
  tipster: {
    id: string;
    name: string;
    avatar?: string;
    country: string;
    roi: number;
    winRate: number;
    totalTips: number;
    isPremium?: boolean;
    isVerified?: boolean;
    streak?: number;
  };
}

interface EnhancedLeaderboardProps {
  title?: string;
  subtitle?: string;
  showFilters?: boolean;
}

export function EnhancedLeaderboard({ 
  title = "Global Leaderboard", 
  subtitle = "Top tipsters ranked by performance",
  showFilters = true 
}: EnhancedLeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("monthly");
  const [selectedSport, setSelectedSport] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("");

  const sports = ["Football", "Basketball", "Tennis", "Baseball", "Hockey"];
  const regions = ["Global", "North America", "Europe", "Asia", "South America"];
  const periods = [
    { key: "daily", label: "Today", icon: Calendar },
    { key: "weekly", label: "This Week", icon: Calendar },
    { key: "monthly", label: "This Month", icon: Calendar },
  ];

  useEffect(() => {
    fetchLeaderboard();
  }, [activeTab, selectedSport, selectedRegion]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('get-leaderboard', {
        body: {
          period: activeTab,
          sport: selectedSport === "all" ? null : selectedSport,
          region: selectedRegion === "Global" ? null : selectedRegion,
          limit: 50
        }
      });

      if (error) throw error;
      
      if (data?.entries && data.entries.length > 0) {
        // Deduplicate entries by tipster ID, keeping highest rank
        const uniqueMap = new Map<string, LeaderboardEntry>();
        data.entries.forEach((entry: LeaderboardEntry) => {
          const existing = uniqueMap.get(entry.tipster.id);
          if (!existing || entry.rank < existing.rank) {
            uniqueMap.set(entry.tipster.id, entry);
          }
        });
        
        // Sort by rank and re-index
        const dedupedEntries = Array.from(uniqueMap.values())
          .sort((a, b) => a.rank - b.rank)
          .map((entry, index) => ({ ...entry, rank: index + 1 }));
        
        setEntries(dedupedEntries);
      } else {
        // Use sample data when no real data available
        const sampleEntries: LeaderboardEntry[] = sampleTipsters.map((tipster, index) => ({
          rank: index + 1,
          tipster: tipster
        }));
        setEntries(sampleEntries);
      }
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      // Use sample data on error
      const sampleEntries: LeaderboardEntry[] = sampleTipsters.map((tipster, index) => ({
        rank: index + 1,
        tipster: tipster
      }));
      setEntries(sampleEntries);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number, isPremium?: boolean) => {
    const iconClass = "h-5 w-5";
    
    if (rank === 1) {
      return (
        <div className="relative">
          <Trophy className={`${iconClass} text-premium`} />
          {isPremium && <Crown className="h-3 w-3 text-premium absolute -top-1 -right-1" />}
        </div>
      );
    }
    if (rank === 2) {
      return <Medal className={`${iconClass} text-muted-foreground`} />;
    }
    if (rank === 3) {
      return <Award className={`${iconClass} text-loss`} />;
    }
    
    return (
      <div className="flex items-center justify-center w-8 h-8 bg-background/50 rounded-full">
        <span className="text-sm font-bold text-muted-foreground">#{rank}</span>
      </div>
    );
  };

  const getRoiColor = (roi: number) => {
    if (roi > 0) return "text-profit";
    if (roi < 0) return "text-loss";
    return "text-muted-foreground";
  };

  const formatFlag = (countryCode: string) => {
    const flagMap: { [key: string]: string } = {
      'US': '🇺🇸', 'UK': '🇬🇧', 'BR': '🇧🇷', 'ES': '🇪🇸', 
      'FR': '🇫🇷', 'DE': '🇩🇪', 'IT': '🇮🇹', 'AR': '🇦🇷',
      'CA': '🇨🇦', 'AU': '🇦🇺', 'MX': '🇲🇽', 'JP': '🇯🇵'
    };
    return flagMap[countryCode.toUpperCase()] || "🌍";
  };

  const getStreakIndicator = (streak: number) => {
    if (streak >= 5) {
      return <Flame className="h-4 w-4 text-profit" />;
    }
    return null;
  };

  return (
    <Card className="bg-gradient-card border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <TrendingUp className="h-5 w-5 text-primary" />
              {title}
            </CardTitle>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          
          {showFilters && (
            <div className="flex items-center gap-2">
              <Select value={selectedSport} onValueChange={setSelectedSport}>
                <SelectTrigger className="w-32" aria-label="Filter by sport">
                  <SelectValue placeholder="Sport" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sports</SelectItem>
                  {sports.map(sport => (
                    <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="w-32" aria-label="Filter by region">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>
                      {region === "Global" ? (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Global
                        </div>
                      ) : (
                        region
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Period Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-3">
            {periods.map(({ key, label, icon: Icon }) => (
              <TabsTrigger key={key} value={key} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {periods.map(({ key }) => (
            <TabsContent key={key} value={key} className="space-y-3 animate-fade-in">
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg">
                        <div className="w-8 h-8 bg-muted rounded-full"></div>
                        <div className="w-10 h-10 bg-muted rounded-full"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted rounded w-1/3"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                        </div>
                        <div className="w-16 h-6 bg-muted rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Loading tipster data...</p>
                </div>
              ) : (
                entries.map((entry, index) => (
                  <div
                    key={entry.tipster.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 hover:bg-background/50 group ${
                      entry.rank <= 3 
                        ? 'border border-border/30 shadow-sm' 
                        : ''
                    } ${
                      entry.rank === 1 
                        ? 'bg-gradient-to-r from-premium/5 to-transparent' 
                        : ''
                    }`}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    {/* Rank */}
                    <div className="flex items-center justify-center w-10">
                      {getRankIcon(entry.rank, entry.tipster.isPremium)}
                    </div>

                    {/* Avatar & Name */}
                    <Avatar className={`h-10 w-10 ring-1 ring-border ${
                      entry.rank === 1 ? 'ring-2 ring-premium' : ''
                    }`}>
                      <AvatarImage src={entry.tipster.avatar} alt={entry.tipster.name} />
                      <AvatarFallback className="bg-primary/10 text-foreground text-xs font-semibold">
                        {entry.tipster.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
                          {entry.tipster.name}
                        </h4>
                        {entry.tipster.isVerified && (
                          <div className="w-4 h-4 bg-profit rounded-full flex items-center justify-center">
                            <Trophy className="h-2.5 w-2.5 text-profit-foreground" />
                          </div>
                        )}
                        {entry.tipster.isPremium && (
                          <Badge className="bg-gradient-premium border-premium/30 text-premium-foreground text-xs">
                            VIP
                          </Badge>
                        )}
                        {entry.tipster.streak && getStreakIndicator(entry.tipster.streak)}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{formatFlag(entry.tipster.country)} {entry.tipster.country}</span>
                        <span>•</span>
                        <span>{entry.tipster.totalTips} tips</span>
                        {entry.tipster.streak && (
                          <>
                            <span>•</span>
                            <span className={entry.tipster.streak > 0 ? "text-profit" : "text-loss"}>
                              {entry.tipster.streak > 0 ? '+' : ''}{entry.tipster.streak} streak
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="text-right space-y-1">
                      <div className={`text-sm font-bold ${getRoiColor(entry.tipster.roi)}`}>
                        {entry.tipster.roi > 0 ? '+' : ''}{entry.tipster.roi}%
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.tipster.winRate}% win rate
                      </div>
                      
                      {/* Mini progress bar for win rate */}
                      <div className="w-16">
                        <Progress 
                          value={entry.tipster.winRate} 
                          className="h-1"
                        />
                      </div>
                    </div>

                    {/* Rank change indicator (placeholder for future feature) */}
                    {entry.rank <= 10 && (
                      <div className="w-6 flex justify-center">
                        {Math.random() > 0.5 ? (
                          <TrendingUp className="h-3 w-3 text-profit" />
                        ) : (
                          <div className="w-3 h-3 bg-muted-foreground/20 rounded-full" />
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Load More Button */}
        {!loading && entries.length >= 20 && (
          <div className="text-center pt-4">
            <Button variant="outline" className="w-full">
              Load More
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}